import { EventOption, HistoricalRecord, Registrant, TripParticipant } from '../types';
import { getNameCandidates } from '../services/verificationService';
import { getEventStartDate } from './dateUtils';
import { getChurchKeys, getRecordChurchKeys } from './churchMatching';
import { getContactKeys, hashContactKey } from './contactHash';

/**
 * 「第一次參與」判斷：查無出隊紀錄、動員聚會紀錄與過往報名紀錄。
 *
 * 三種來源（皆以姓名比對，沿用 verificationService 的 getNameCandidates）：
 * 1. historical_records：動員聚會出席紀錄（yearAttended ≤ 梯隊年份；0 = 年份未詳，一律算）
 * 2. trip_participants：過往出隊名單（years 中有早於梯隊年份者；years 空白 = 年份未詳，一律算）
 * 3. registrations：同名者在「開始日早於本梯隊」的其他梯隊／動員聚會已遴選通過（isApproved）
 *
 * 每筆同名紀錄是否算「參與過」（churchAware，預設開啟）：
 * - 任一邊沒有可用教會，或教會有交集 → 算（不看聯絡資料）
 * - 教會衝突（兩邊都有教會且無交集）→ 才看手機／email：
 *   - 兩邊都有聯絡資料：有交集 → 同一人（轉會）→ 算；完全不交集 → 同名不同人 → 這筆不算
 *   - 任一邊沒有聯絡資料 → 無法分辨 → 算（實測轉會約 27%，同名不同人約 1%）
 *
 * 教會：紀錄取 `churches`（補充欄位）→ 否則 `church`；報名者取自己所有報名
 * （同姓名且同手機或同 email）填過的教會。
 * 聯絡資料：紀錄取 `contactHashes`（由總名冊補充，只存雜湊）；過往報名直接用其手機／email；
 * 報名者用本次＋同姓名同手機／email 其他報名的手機／email。正規化與雜湊見 utils/contactHash.ts。
 *
 * 結果只有兩種：查無任何「算」的紀錄 → 第一次參與；否則不是。
 */

export const FIRST_TIME_PARTICIPANT_TOOLTIP = '查無出隊紀錄、動員聚會紀錄與過往報名紀錄';

export interface FirstTimeIndexOptions {
  /** false = 只比姓名（舊行為），不看教會與聯絡資料 */
  churchAware?: boolean;
}

const UNKNOWN_YEAR = Number.NEGATIVE_INFINITY;

export type PriorRecordSource = 'historical_records' | 'trip_participants' | 'registrations';

/** 一筆同名紀錄：年份／開始時間、教會 key、聯絡資料雜湊 */
export interface PriorRecordEntry {
  source: PriorRecordSource;
  id: string;
  /** 年份（年份未詳為 -Infinity）或已通過報名的梯隊開始時間（ms） */
  value: number;
  churches: string[];
  contactHashes: string[];
}

/** 同名紀錄與報名者的關係 */
export type PriorRecordRelation =
  | 'no-church'        // 任一邊沒有可用教會 → 算
  | 'same-church'      // 教會有交集 → 算
  | 'contact-overlap'  // 教會衝突、聯絡資料有交集（轉會）→ 算
  | 'contact-missing'  // 教會衝突、任一邊沒有聯絡資料 → 算
  | 'contact-disjoint'; // 教會衝突、聯絡資料完全不交集（同名不同人）→ 不算

interface PersonProfile {
  churches: Set<string>;
  contactKeys: Set<string>;
}

export interface FirstTimeParticipantIndex {
  /** 姓名候選 → 動員聚會紀錄 */
  historical: Map<string, PriorRecordEntry[]>;
  /** 姓名候選 → 出隊紀錄 */
  trip: Map<string, PriorRecordEntry[]>;
  /** 姓名候選 → 已通過的報名 */
  approved: Map<string, PriorRecordEntry[]>;
  /** eventId → 開始時間（ms），無法解析者不列入 */
  eventStartById: Map<string, number>;
  /** 「姓名候選|聯絡 key」→ 此人所有報名填過的教會與聯絡 key（轉會、多處聚會、換手機） */
  persons: Map<string, PersonProfile>;
  churchAware: boolean;
}

type RegistrantLike = Pick<Registrant, 'name'> & Partial<Pick<Registrant, 'church' | 'phone' | 'email'>>;

const addEntry = (map: Map<string, PriorRecordEntry[]>, candidate: string, entry: PriorRecordEntry) => {
  const list = map.get(candidate);
  if (list) list.push(entry);
  else map.set(candidate, [entry]);
};

const toKnownYear = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : UNKNOWN_YEAR;
};

const toHashList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((hash): hash is string => typeof hash === 'string' && hash.length > 0) : [];

/** 同姓名同手機／email 的所有報名合併出的教會與聯絡 key */
const lookupProfile = (person: RegistrantLike, persons: Map<string, PersonProfile>): PersonProfile => {
  const churches = new Set(getChurchKeys(person.church));
  const contactKeys = new Set(getContactKeys(person));
  const ownContacts = Array.from(contactKeys);
  getNameCandidates(person.name || '').forEach(candidate => ownContacts.forEach(contact => {
    const profile = persons.get(`${candidate}|${contact}`);
    if (!profile) return;
    profile.churches.forEach(church => churches.add(church));
    profile.contactKeys.forEach(key => contactKeys.add(key));
  }));
  return { churches, contactKeys };
};

export const buildFirstTimeParticipantIndex = (
  historicalRecords: HistoricalRecord[],
  tripParticipants: TripParticipant[],
  registrations: Registrant[],
  events: EventOption[],
  options: FirstTimeIndexOptions = {}
): FirstTimeParticipantIndex => {
  const churchAware = options.churchAware !== false;
  const historical = new Map<string, PriorRecordEntry[]>();
  const trip = new Map<string, PriorRecordEntry[]>();
  const approved = new Map<string, PriorRecordEntry[]>();
  const eventStartById = new Map<string, number>();
  const persons = new Map<string, PersonProfile>();
  const churchesOf = (record: { church?: unknown; churches?: unknown }) =>
    churchAware ? getRecordChurchKeys(record) : [];
  const recordHashes = (record: { contactHashes?: unknown }) => (churchAware ? toHashList(record.contactHashes) : []);

  historicalRecords.forEach(record => {
    if (!record?.name) return;
    const entry: PriorRecordEntry = {
      source: 'historical_records',
      id: record.id,
      value: toKnownYear(record.yearAttended),
      churches: churchesOf(record),
      contactHashes: recordHashes(record),
    };
    getNameCandidates(record.name).forEach(candidate => addEntry(historical, candidate, entry));
  });

  tripParticipants.forEach(participant => {
    if (!participant?.name) return;
    const knownYears = (Array.isArray(participant.years) ? participant.years : [])
      .map(toKnownYear)
      .filter(year => year !== UNKNOWN_YEAR);
    const entry: PriorRecordEntry = {
      source: 'trip_participants',
      id: participant.id,
      value: knownYears.length > 0 ? Math.min(...knownYears) : UNKNOWN_YEAR,
      churches: churchesOf(participant),
      contactHashes: recordHashes(participant),
    };
    getNameCandidates(participant.name).forEach(candidate => addEntry(trip, candidate, entry));
  });

  events.forEach(event => {
    const start = getEventStartDate(event);
    if (start) eventStartById.set(event.id, start.getTime());
  });

  if (churchAware) {
    registrations.forEach(registration => {
      if (!registration?.name) return;
      const churches = getChurchKeys(registration.church);
      const contacts = getContactKeys(registration);
      getNameCandidates(registration.name).forEach(candidate => contacts.forEach(contact => {
        const key = `${candidate}|${contact}`;
        let profile = persons.get(key);
        if (!profile) {
          profile = { churches: new Set(), contactKeys: new Set() };
          persons.set(key, profile);
        }
        churches.forEach(church => profile!.churches.add(church));
        contacts.forEach(other => profile!.contactKeys.add(other));
      }));
    });
  }

  registrations.forEach(registration => {
    if (!registration?.isApproved || !registration.name) return;
    const starts = (registration.selectedEvents || [])
      .map(eventId => eventStartById.get(eventId))
      .filter((time): time is number => time !== undefined);
    if (starts.length === 0) return;
    const entry: PriorRecordEntry = {
      source: 'registrations',
      id: registration.id,
      value: Math.min(...starts),
      churches: churchesOf(registration),
      contactHashes: churchAware
        ? Array.from(lookupProfile(registration, persons).contactKeys).map(hashContactKey)
        : [],
    };
    getNameCandidates(registration.name).forEach(candidate => addEntry(approved, candidate, entry));
  });

  return { historical, trip, approved, eventStartById, persons, churchAware };
};

export interface RegistrantMatchProfile {
  /** 教會 key：本次填的教會 + 同姓名同手機／email 其他報名填過的教會；本次沒填可用教會 → [] */
  churches: string[];
  /** 聯絡資料雜湊：本次 + 同姓名同手機／email 其他報名的手機／email */
  contactHashes: Set<string>;
}

export const getRegistrantMatchProfile = (
  registrant: RegistrantLike,
  index: FirstTimeParticipantIndex
): RegistrantMatchProfile => {
  if (!index.churchAware) return { churches: [], contactHashes: new Set() };
  const profile = lookupProfile(registrant, index.persons);
  const ownChurches = getChurchKeys(registrant.church);
  return {
    churches: ownChurches.length > 0 ? Array.from(profile.churches) : [],
    contactHashes: new Set(Array.from(profile.contactKeys).map(hashContactKey)),
  };
};

/** 報名者的教會 key（見 getRegistrantMatchProfile） */
export const getRegistrantChurchKeys = (registrant: RegistrantLike, index: FirstTimeParticipantIndex): string[] =>
  getRegistrantMatchProfile(registrant, index).churches;

export const getPriorRecordRelation = (entry: PriorRecordEntry, registrant: RegistrantMatchProfile): PriorRecordRelation => {
  if (registrant.churches.length === 0 || entry.churches.length === 0) return 'no-church';
  if (entry.churches.some(church => registrant.churches.includes(church))) return 'same-church';
  if (registrant.contactHashes.size === 0 || entry.contactHashes.length === 0) return 'contact-missing';
  return entry.contactHashes.some(hash => registrant.contactHashes.has(hash)) ? 'contact-overlap' : 'contact-disjoint';
};

/** 這筆紀錄是否算「參與過」：只有 'contact-disjoint'（同名不同人）不算 */
export const priorRecordCounts = (relation: PriorRecordRelation): boolean => relation !== 'contact-disjoint';

export interface PriorRecordEvidence extends PriorRecordEntry {
  relation: PriorRecordRelation;
  counts: boolean;
}

type Qualifies = (value: number) => boolean;

const qualifyingSources = (
  selectedEvent: EventOption | undefined | null,
  index: FirstTimeParticipantIndex
): Array<[Map<string, PriorRecordEntry[]>, Qualifies]> => {
  const eventYear = selectedEvent?.year || Number.POSITIVE_INFINITY;
  const eventStart = selectedEvent ? index.eventStartById.get(selectedEvent.id) : undefined;
  return [
    [index.historical, year => year <= eventYear],
    [index.trip, year => year < eventYear],
    // 開始日嚴格早於本梯隊 → 必為不同梯隊；本梯隊開始日無法解析時略過此項
    [index.approved, start => eventStart !== undefined && start < eventStart],
  ];
};

/** 列出所有時間上符合的同名紀錄與判斷結果（腳本評估／除錯用） */
export const getFirstTimeParticipantEvidence = (
  registrant: RegistrantLike,
  selectedEvent: EventOption | undefined | null,
  index: FirstTimeParticipantIndex
): PriorRecordEvidence[] => {
  const profile = getRegistrantMatchProfile(registrant, index);
  const seen = new Set<PriorRecordEntry>();
  const out: PriorRecordEvidence[] = [];
  const sources = qualifyingSources(selectedEvent, index);
  getNameCandidates(registrant.name || '').forEach(candidate => sources.forEach(([map, qualifies]) => {
    (map.get(candidate) || []).forEach(entry => {
      if (seen.has(entry) || !qualifies(entry.value)) return;
      seen.add(entry);
      const relation = getPriorRecordRelation(entry, profile);
      out.push({ ...entry, relation, counts: priorRecordCounts(relation) });
    });
  }));
  return out;
};

/** 顯示「第一次參與」徽章：查無任何算數的同名紀錄。 */
export const isFirstTimeParticipantWithIndex = (
  registrant: RegistrantLike,
  selectedEvent: EventOption | undefined | null,
  index: FirstTimeParticipantIndex
): boolean => {
  const candidates = getNameCandidates(registrant.name || '');
  if (candidates.length === 0) return false;
  const sources = qualifyingSources(selectedEvent, index);
  let profile: RegistrantMatchProfile | null = null;
  for (const candidate of candidates) {
    for (const [map, qualifies] of sources) {
      for (const entry of map.get(candidate) || []) {
        if (!qualifies(entry.value)) continue;
        profile = profile || getRegistrantMatchProfile(registrant, index);
        if (priorRecordCounts(getPriorRecordRelation(entry, profile))) return false;
      }
    }
  }
  return true;
};

/** 單次判斷用；大量判斷請先 buildFirstTimeParticipantIndex 再用 isFirstTimeParticipantWithIndex。 */
export const isFirstTimeParticipant = (
  registrant: RegistrantLike,
  selectedEvent: EventOption | undefined | null,
  historicalRecords: HistoricalRecord[],
  tripParticipants: TripParticipant[],
  registrations: Registrant[],
  events: EventOption[],
  options: FirstTimeIndexOptions = {}
): boolean =>
  isFirstTimeParticipantWithIndex(
    registrant,
    selectedEvent,
    buildFirstTimeParticipantIndex(historicalRecords, tripParticipants, registrations, events, options)
  );
